import { Box } from '@mui/material';
import { Mail } from 'lucide-react';
import { useRef, useState, type ReactElement, type SyntheticEvent } from 'react';

import { useHttpClient } from '@/app/providers';
import { Field } from '@/ui/kit';

import { AuthLayout } from './AuthLayout';
import { AuthLink } from './AuthLink';
import { AuthNotice } from './AuthNotice';
import { AuthSubmitButton } from './AuthSubmitButton';
import { LOGIN_PATH, RESET_PASSWORD_PATH } from './routes';

/**
 * Premier ecran de la recuperation : « Mot de passe oublié ».
 *
 * Demande un code par e-mail (`POST /auth/forgot-password`). **La confirmation
 * est toujours la meme**, que l'adresse existe ou non : le serveur repond 202 dans
 * les deux cas pour ne pas servir d'oracle sur les comptes, et cet ecran ne
 * doit pas le reintroduire. Les seules erreurs affichees sont celles qui ne
 * dependent pas de l'adresse : trop de tentatives (429) et courriel non remis
 * (503, `mail_delivery_failed`).
 *
 * Le code est saisi sur l'ecran suivant, atteint par un lien : l'enchainer
 * automatiquement ferait croire qu'un code est parti meme quand ce n'est pas le cas.
 */

export const CONFIRMATION = 'Si un compte existe pour cette adresse, un code vient de lui être envoyé.';

export const ForgotPasswordPage = (): ReactElement => {
  const http = useHttpClient();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (inFlight.current || email.trim() === '') {
      return;
    }

    inFlight.current = true;
    setError(null);
    setSent(false);
    setSubmitting(true);

    http
      .request({ method: 'POST', path: '/auth/forgot-password', body: { email: email.trim() }, anonymous: true })
      .then(() => {
        setSent(true);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "La demande n'a pas abouti. Réessayez.");
      })
      .finally(() => {
        inFlight.current = false;
        setSubmitting(false);
      });
  };

  return (
    <AuthLayout
      title="Mot de passe oublié"
      documentTitle="Mot de passe oublié"
      subtitle="Indiquez l’adresse de votre compte pour recevoir un code."
    >
      {error === null ? null : <AuthNotice tone="error">{error}</AuthNotice>}
      {sent ? <AuthNotice tone="success">{CONFIRMATION}</AuthNotice> : null}

      <Box component="form" onSubmit={submit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <Field
          label="E-mail"
          type="email"
          size="L"
          icon={Mail}
          value={email}
          onChange={setEmail}
          placeholder="vous@exemple.fr"
          autoComplete="username"
          autoFocus
          required
        />

        <AuthSubmitButton busy={submitting} disabled={email.trim() === ''}>
          {submitting ? 'Envoi…' : 'Envoyer le code'}
        </AuthSubmitButton>
      </Box>

      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1.5 }}>
        <AuthLink to={LOGIN_PATH}>Retour à la connexion</AuthLink>
        <AuthLink to={RESET_PASSWORD_PATH}>J’ai déjà un code</AuthLink>
      </Box>
    </AuthLayout>
  );
};
